/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { getSelectedProductIdFromActionInput } from '@msdyn365-commerce-modules/retail-actions';
import {
    getByIdAsync,
    getVariantsByDimensionValuesAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    ProductDimension,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';


export class GetProductRecordidInput implements Msdyn365.IActionInput {
    public productId: number;
    public channelId: number;
    public matchingDimensionValues: ProductDimension[];

    constructor(productId: number, channelId: number, matchingDimensionValues?: ProductDimension[]) {
        this.productId = productId;
        this.channelId = channelId;
        this.matchingDimensionValues = matchingDimensionValues || [];
    }

    public getCacheKey = () => `GetProductRecord`;
    public getCacheObjectType = () => 'SimpleProduct';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

// TODO: Create a data model here or import one to capture the response of the action
export interface IGetProductRecordidData {
    text: string;
}

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createInput = (inputData: Msdyn365.ICreateActionContext): GetProductRecordidInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);
    if (productId) {
        return new GetProductRecordidInput(+productId, +inputData.requestContext.apiSettings.channelId, []);
    } else {
        throw new Error('Unable to create SelectedVariantInput, no productId found on module config or query');
    }
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */
async function action(input: GetProductRecordidInput, ctx: Msdyn365.IActionContext): Promise<SimpleProduct[]> {
    let baseProduct: SimpleProduct | null = null;
    if (!ctx.requestContext.app.config.queryBasedVariantSelection) {
        const response = await getByIdAsync({ callerContext: ctx }, input.productId, input.channelId);
        baseProduct = Array.isArray(response) ? response[0] : response;
    }
    const matchingDimensionValues = input.matchingDimensionValues ?? [];
    const data = await getVariantsByDimensionValuesAsync(
        { callerContext: ctx, queryResultSettings: {} },
        baseProduct && baseProduct?.MasterProductId ? baseProduct.MasterProductId : input.productId,
        input.channelId,
        matchingDimensionValues
    );

    return data;
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<SimpleProduct[]>>action,
    // TODO: Give your data action a unique id
    id: 'GetProductRecordId',
    input: createInput
    // TODO: Uncomment the below line if this is a meant to be a batched data action
    // isBatched: true
});
